"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.HistogramComponent = exports.getTransformationAttribute = void 0;
var d3 = require("d3");
var React = require("react");
var react_1 = require("react");
var styles = require("../styles/ChartStyles");
var colors = require("../util/colors");
var sizes = require("../util/sizes");
var utils_1 = require("../util/utils");
/**
 * A helper function to create translate attribute
 * @param x x axis coordinate
 * @param y y axis coordinate
 * @returns translate attribute string value
 */
var getTransformationAttribute = function (x, y) {
    return "translate(" + x + "," + y + ")";
};
exports.getTransformationAttribute = getTransformationAttribute;
var formatCount = d3.format(',.0f');
var CHART_CONFIG = {
    fontSize: '12px',
};
var HistogramComponent = function (props) {
    var bins = props.bins, yAxisLabel = props.yAxisLabel, xAxisLabel = props.xAxisLabel, color = props.color;
    var chartRef = react_1.useRef(null);
    var heightRegular = sizes.heightRegular, widthRegular = sizes.widthRegular, margin = sizes.chartMargins;
    var height = props.height || heightRegular;
    var width = props.width || widthRegular;
    var chartHeight = height - margin.top - margin.bottom;
    var chartWidth = width - margin.left - margin.right;
    var xAxisTranslate = height - margin.bottom;
    var yAxisTranslate = margin.left;
    var numBins = Math.max(bins.length, 1);
    var min = bins.length ? Number(bins[0].name) : 0;
    var max = bins.length ? Number(bins[bins.length - 1].name) : 0;
    var delta = (max - min) / numBins;
    var yScale = d3
        .scaleLinear()
        .domain([0, Math.max.apply(Math, bins.map(function (b) { return b.count; }))])
        .range([height - margin.bottom, margin.top]);
    var xScale = d3
        .scaleLinear()
        .range([margin.left, width - margin.right])
        .domain([min, max + delta]); // + delta to ensure we have enough space
    var getScalePosition = function (scale, num) {
        var _a;
        if (num === void 0) { num = 0; }
        return (_a = scale(num)) !== null && _a !== void 0 ? _a : 0;
    };
    var drawAxes = react_1.useCallback(function () {
        var svg = d3.select(chartRef.current);
        var yAxis = utils_1.getLinearAxis(yScale, utils_1.AxisPosition.Left);
        utils_1.formatAxis(yAxis, 5, 0, 7, utils_1.TickFormat.formatK);
        utils_1.drawAxis(svg, yAxis, yAxisTranslate, 0, CHART_CONFIG.fontSize, styles.barAxisText);
        var xAxis = utils_1.getLinearAxis(xScale, utils_1.AxisPosition.Bottom);
        utils_1.formatAxis(xAxis, bins.length, sizes.defaultTickSize, sizes.defaultTickPadding, utils_1.TickFormat.formatK);
        utils_1.drawAxis(svg, xAxis, 0, xAxisTranslate, CHART_CONFIG.fontSize, styles.barAxisText);
        var ticks = d3.select(chartRef.current).selectAll('.tick text');
        ticks.each(function (_, i) {
            // Show less ticks based on number of bins
            if (i % (numBins / 5) !== 0)
                d3.select(this).remove();
        });
        // draw background lines
        var backgroundAxis = utils_1.getLinearAxis(yScale, utils_1.AxisPosition.Left);
        utils_1.formatAxis(backgroundAxis, 5, -chartWidth, sizes.defaultTickPadding, utils_1.TickFormat.hideNumbers);
        utils_1.drawAxisGridLines(svg, backgroundAxis, yAxisTranslate, 0);
    }, [bins.length, chartWidth, numBins, xAxisTranslate, xScale, yAxisTranslate, yScale]);
    var drawBars = react_1.useCallback(function () {
        var bars = d3.select(chartRef.current).selectAll('rect').data(bins).enter();
        bars
            .append('rect')
            .attr('x', 1)
            .attr('transform', function (d) {
            var repositionX = getScalePosition(xScale, Number(d.name));
            var repositionY = getScalePosition(yScale, d.count);
            return exports.getTransformationAttribute(repositionX, repositionY);
        })
            .attr('width', function (d) {
            var startReposition = getScalePosition(xScale, Number(d.name));
            var endReposition = getScalePosition(xScale, Number(d.name) + delta);
            return endReposition - startReposition - 1;
        })
            .attr('height', function (d) { return xAxisTranslate - getScalePosition(yScale, d.count); })
            .style('fill', color || colors.background01);
        bars
            .append('text')
            .attr('class', styles.barStatusText)
            .attr('text-anchor', 'middle')
            .attr('x', function (d) {
            var startReposition = getScalePosition(xScale, Number(d.name));
            var endReposition = getScalePosition(xScale, Number(d.name) + delta);
            return (startReposition + endReposition) / 2;
        })
            .attr('y', function (d) {
            return getScalePosition(yScale, d.count) - 1;
        })
            .text(function (d) {
            return formatCount(d.count);
        });
    }, [bins, xAxisTranslate, color, xScale, yScale, delta]);
    react_1.useEffect(function () {
        d3.select(chartRef.current).selectAll('*').remove();
        var svg = d3.select(chartRef.current).attr('height', height).attr('width', width);
        drawAxes();
        drawBars();
        var xAxisLabelStartX = (chartWidth + margin.left + margin.right) / 2;
        var xAxisLabelStartY = chartHeight + margin.top + margin.bottom;
        utils_1.drawAxisLabel(svg, xAxisLabel || '', xAxisLabelStartX, xAxisLabelStartY, false, CHART_CONFIG.fontSize, styles.barAxisText);
        var yAxisLabelStartX = 10;
        var yAxisLabelStartY = chartHeight / 2 + margin.top;
        utils_1.drawAxisLabel(svg, yAxisLabel || '', yAxisLabelStartX, yAxisLabelStartY, true, CHART_CONFIG.fontSize, styles.barAxisText);
    }, [chartHeight, chartWidth, drawAxes, drawBars, height, margin, width, xAxisLabel, yAxisLabel]);
    return (React.createElement("div", null,
        React.createElement("svg", { ref: chartRef })));
};
exports.HistogramComponent = HistogramComponent;
//# sourceMappingURL=HistogramComponent.js.map